// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf gizmo's numbers (frontend-aev.7): snapping and the rigid
 * delta the server applies. Pure and DOM-free.
 *
 * The delta is ruxd's `RigidDelta` (PR #19): world-frame `xyz` plus yaw /
 * pitch / roll in degrees, R = Rz(yaw) Ry(pitch) Rx(roll), about `pivot`:
 * p' = pivot + xyz + R (p − pivot). Matrices are row-major 4×4, like the
 * graph's node poses and the preview's `frames[].after`.
 */

import type { BlockTransformRequest, RigidDelta } from '../api/types';
import type { Vec3 } from './rig';

/** The gizmo's delta, as the numeric fields show it (m, deg). */
export interface GizmoDelta {
  dx: number;
  dy: number;
  dz: number;
  yaw: number;
  pitch: number;
  roll: number;
}

export const ZERO_DELTA: GizmoDelta = { dx: 0, dy: 0, dz: 0, yaw: 0, pitch: 0, roll: 0 };

export const XY_STEP_M = 0.05;
export const YAW_STEP_DEG = 90;
export const YAW_FINE_STEP_DEG = 1;

export function isZeroDelta(d: GizmoDelta, eps = 1e-9): boolean {
  return Math.abs(d.dx) < eps && Math.abs(d.dy) < eps && Math.abs(d.dz) < eps && Math.abs(d.yaw) < eps && Math.abs(d.pitch) < eps && Math.abs(d.roll) < eps;
}

const clean = (v: number) => (Object.is(v, -0) ? 0 : v);

/** Snap a step: `v` to the nearest multiple of `step`. */
export function snapStep(v: number, step: number): number {
  if (!(step > 0)) return v;
  return clean(Math.round(v / step) * step);
}

/** Yaw to 90° steps (0/90/180/270), or 1° with the fine modifier (shift). */
export function snapYawDeg(deg: number, fine = false): number {
  return snapStep(deg, fine ? YAW_FINE_STEP_DEG : YAW_STEP_DEG);
}

/** x / y to 5 cm. */
export function snapXY(v: number, step = XY_STEP_M): number {
  return snapStep(v, step);
}

/**
 * One storey's height from the camera-height bands (`storey_levels`): the
 * median spacing of consecutive bands; null with fewer than two.
 */
export function storeyHeight(levels: readonly number[]): number | null {
  const sorted = [...levels].sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i += 1) gaps.push(sorted[i] - sorted[i - 1]);
  if (gaps.length === 0) return null;
  gaps.sort((a, b) => a - b);
  const m = gaps.length >> 1;
  return gaps.length % 2 ? gaps[m] : (gaps[m - 1] + gaps[m]) / 2;
}

/**
 * Snap a vertical move: the selection's camera height `baseZ` plus `dz`
 * lands on the nearest storey level (a camera-height band) or a whole
 * number of storey heights from where it started. Returns the snapped dz
 * (0 for a pull that stays nearest to the start).
 */
export function snapZ(dz: number, baseZ: number, levels: readonly number[]): number {
  const h = storeyHeight(levels);
  // A band within 2 cm of where the selection is counts as "here": no creep.
  const candidates = levels.filter((l) => Math.abs(l - baseZ) > 0.02);
  candidates.push(baseZ);
  if (h) for (let k = -4; k <= 4; k += 1) if (k !== 0) candidates.push(baseZ + k * h);
  const target = baseZ + dz;
  let best = candidates[0];
  for (const c of candidates) if (Math.abs(c - target) < Math.abs(best - target)) best = c;
  return clean(best - baseZ);
}

/** The modifier state the gizmo snaps under. */
export interface SnapOptions {
  /** Snapping on at all (the toolbar toggle). */
  enabled: boolean;
  /** Fine yaw step (shift held). */
  fine: boolean;
  /** Camera-height bands for z. */
  levels: readonly number[];
  /** Mean camera height of the selection. */
  baseZ: number;
}

/** Apply every snapping rule to a raw gizmo delta. Pitch and roll are never snapped. */
export function snapDelta(d: GizmoDelta, opts: SnapOptions): GizmoDelta {
  if (!opts.enabled) return d;
  return {
    ...d,
    dx: snapXY(d.dx),
    dy: snapXY(d.dy),
    dz: opts.levels.length > 0 ? snapZ(d.dz, opts.baseZ, opts.levels) : d.dz,
    yaw: snapYawDeg(d.yaw, opts.fine),
  };
}

const RAD = Math.PI / 180;

/** R = Rz(yaw) Ry(pitch) Rx(roll), row-major 3×3. */
export function rotationMatrix(yawDeg: number, pitchDeg: number, rollDeg: number): number[] {
  const [cz, sz] = [Math.cos(yawDeg * RAD), Math.sin(yawDeg * RAD)];
  const [cy, sy] = [Math.cos(pitchDeg * RAD), Math.sin(pitchDeg * RAD)];
  const [cx, sx] = [Math.cos(rollDeg * RAD), Math.sin(rollDeg * RAD)];
  return [
    cz * cy, cz * sy * sx - sz * cx, cz * sy * cx + sz * sx,
    sz * cy, sz * sy * sx + cz * cx, sz * sy * cx - cz * sx,
    -sy, cy * sx, cy * cx,
  ];
}

/** The full delta about `pivot`, row-major 4×4. */
export function deltaMatrix(d: GizmoDelta, pivot: Vec3): number[] {
  const r = rotationMatrix(d.yaw, d.pitch, d.roll);
  const out = new Array<number>(16).fill(0);
  for (let i = 0; i < 3; i += 1) {
    for (let j = 0; j < 3; j += 1) out[i * 4 + j] = r[i * 3 + j];
    const rp = r[i * 3] * pivot[0] + r[i * 3 + 1] * pivot[1] + r[i * 3 + 2] * pivot[2];
    out[i * 4 + 3] = pivot[i] + [d.dx, d.dy, d.dz][i] - rp;
  }
  out[15] = 1;
  return out;
}

/** A row-major 4×4 applied to a point. */
export function applyMatrix(m: readonly number[], p: Vec3): Vec3 {
  return [
    m[0] * p[0] + m[1] * p[1] + m[2] * p[2] + m[3],
    m[4] * p[0] + m[5] * p[1] + m[6] * p[2] + m[7],
    m[8] * p[0] + m[9] * p[1] + m[10] * p[2] + m[11],
  ];
}

/** The translation of a row-major 4×4 (a pose's camera position). */
export function matrixTranslation(m: readonly number[]): Vec3 {
  return [m[3], m[7], m[11]];
}

/** The contract's `RigidDelta`: zero components left out, the pivot always sent (what the user saw). */
export function toRigidDelta(d: GizmoDelta, pivot: Vec3): RigidDelta {
  const out: RigidDelta = { xyz: [clean(d.dx), clean(d.dy), clean(d.dz)], pivot };
  if (d.yaw) out.yaw_deg = d.yaw;
  if (d.pitch) out.pitch_deg = d.pitch;
  if (d.roll) out.roll_deg = d.roll;
  return out;
}

export interface ApplyOptions {
  mode: 'reseed' | 'local';
  blendFrames: number;
  hold: boolean;
}

/** The apply / preview request for the selected block ids. */
export function transformRequest(
  blockIds: readonly number[],
  d: GizmoDelta,
  pivot: Vec3,
  opts: ApplyOptions,
  preview: boolean,
  label?: string,
): BlockTransformRequest {
  const req: BlockTransformRequest = {
    blocks: [...blockIds],
    delta: toRigidDelta(d, pivot),
    mode: opts.mode,
    blend_frames: Math.max(0, Math.round(opts.blendFrames)),
  };
  if (opts.hold) req.hold = true;
  if (preview) req.preview = true;
  if (label) req.label = label;
  return req;
}

const num = (v: number, digits: number) =>
  v.toLocaleString('da-DK', { maximumFractionDigits: digits, minimumFractionDigits: 0, signDisplay: 'exceptZero' });

/** "Flyt +1,5 / −0,8 m, drej +12°" — the default pose-version label. */
export function deltaLabel(names: readonly string[], d: GizmoDelta): string {
  const parts: string[] = [];
  if (d.dx || d.dy || d.dz) parts.push(`${num(d.dx, 2)}/${num(d.dy, 2)}/${num(d.dz, 2)} m`);
  if (d.yaw) parts.push(`yaw ${num(d.yaw, 1)}°`);
  if (d.pitch) parts.push(`pitch ${num(d.pitch, 1)}°`);
  if (d.roll) parts.push(`roll ${num(d.roll, 1)}°`);
  const who = names.length <= 2 ? names.join(' + ') : `${names[0]} + ${names.length - 1} blokke`;
  return `${who}: ${parts.join(', ') || 'ingen flytning'}`;
}

/** Parse a typed field (Danish comma or dot); null when it is not a number. */
export function parseNumberField(raw: string): number | null {
  const t = raw.trim().replace(/−/g, '-').replace(',', '.');
  if (t === '' || t === '-' || t === '.') return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}
